import * as THREE from "../../libs/three.js/build/three.module.js";
import {MouseIntent} from "./MouseIntent.js";
import {computeScreenSpaceScale} from "../digitization/ScreenSpaceScale.js";
import {FRONT_PARAMS, createFrontContext, pickFrontSurface} from "../digitization/geometry/frontLayerPick.js";

// "Overfladedetektion" (viewer.detection, switched in Egenskaber -> Interaktion): a small disc on the FOREGROUND
// surface under the mouse, turned to follow the local surface normal - and the same surface is what Digitalisering
// registers/measures on (DigitizationModule._pickPointCloudPosition), so the cursor cannot "pick through" a gap
// between the points of a foreground surface onto geometry behind it.
//
// Method (see digitization/geometry/frontLayerPick.js): ONE lean GPU pick in a small window (21x21 px) -> depth of each
// distinct point along the view ray -> the front-most supported depth layer -> the layer point nearest the cursor
// (placement) and a normal from <= 32 layer points (disc). No node scanning, no neighbourhood search.
//
// The disc is DISPLAY and decoupled from the pick rate: every frame it is placed at (cursor ray) x (last authoritative
// plane); a pick only refreshes that plane, and a new plane is blended in over 40-80 ms (time based, FPS independent).
// frontPick() is the single authoritative result for disc AND placement (cached per mouse position / camera / 500 ms,
// so a click right after the disc was drawn costs no second pick).
//
// Pick scheduling (MouseIntent.js, screen speed): fast sweep (>= 800 px/s) -> at most one pick per 120 ms; slower ->
// cost-adaptive interval (cost / 0.3, 16-100 ms); pointer stops -> one pick at once.
//
// On-demand rendering: the viewer only draws when marked dirty - _markIfChanged() requests a redraw exactly when the
// disc looks different from the last frame.
//
// PROVISIONAL display/throttle parameters (not product rules)
const INDICATOR_RADIUS_PX = 20;        // ~40 px diameter
const OPACITY_CERTAIN = 0.5;
const OPACITY_UNCERTAIN = 0.15;        // dimmed, camera-facing: "hit here, no reliable normal" (0 = hide)
// Analysis interval: cost-adaptive instead of a fixed 50 ms. An analysis may use at most
// ~TARGET_DUTY of the main thread (interval = cost / duty), clamped to one frame ... 100 ms.
// A cheaper analysis therefore directly means a more frequent, more responsive indicator.
const MIN_INTERVAL_MS = 16;
const MAX_INTERVAL_MS = 100;
const TARGET_DUTY = 0.3;
const FADE_OUT_MS = 80;               // disappearance when a fast movement starts
const FADE_IN_MS = 40;                // reappearance after the fresh analysis
const MIN_MOVE_PX = 2;                 // ignore sub-2px jitter
// The disc follows the cursor every frame along the ray/plane intersection with the last authoritative plane; the
// (expensive) pick only refreshes that plane.
const FRONT_FAST_PICK_INTERVAL_MS = 120; // pick rate while the pointer sweeps fast (>= 800 px/s); slow/stopped: cost-adaptive 16-100 ms
const FRONT_EXTRAP_PX = 150;             // the plane is trusted this far (screen px, at the plane's depth) from where it was measured
const FRONT_BLEND_SAME_MS = 80;          // time-based blend to a new plane that is the same surface
const FRONT_BLEND_OTHER_MS = 40;         // ...and to a different surface / depth (short: no long glide through free space)
const FRONT_SAME_SURFACE_PX = 8;         // old and new target within this many px of each other = same surface
const FRONT_MIN_GRAZING = 0.15;          // |ray . normal| below this: plane too edge-on, use a camera-facing plane instead

function now(){ return typeof performance !== "undefined" ? performance.now() : Date.now(); }

export class SurfaceDetection {
	// `deps` (all optional) lets a test replace the clock, the front-surface pick and the screen-scale helper;
	// production uses the defaults.
	constructor(viewer, inputHandler, deps = {}){
		this._now = deps.now || now;
		this._scale = deps.scale || computeScreenSpaceScale;
		this.viewer = viewer;
		this.input = inputHandler;
		this.domElement = viewer.renderer.domElement;

		this.intent = new MouseIntent();
		this.intentGate = true;   // diagnostic switch: false disables the speed gating
		this.fade = 0;            // 0..1 visibility multiplier of the indicator
		this.baseOpacity = OPACITY_CERTAIN;
		this.lastFrameAt = 0;
		this.dirty = false;
		this.navigating = false;
		this.mouse = {x: 0, y: 0};
		this.lastAnalysed = {x: -1e9, y: -1e9};
		this.lastAt = 0;

		this.hit = {x: 0, y: 0, z: 0};
		this.camera = {x: 0, y: 0, z: 0};

		// Counters (cheap, kept as permanent diagnostics - e.g. to verify "1 pick
		// per update" and the update rate from the console:
		// viewer.inputHandler.surfaceDetection.stats)
		this.stats = {
			fast: false, speed: 0, fastEntries: 0, resumes: 0,
			updates: 0, picks: 0, accepted: 0, uncertain: 0, noHit: 0,
			skippedThrottle: 0, skippedNoMove: 0,
			lastPickMs: 0, lastAnalyseMs: 0, lastTotalMs: 0,
			interval: MIN_INTERVAL_MS, lastPoints: 0, lastFailure: null,
		};

		// ONE shared pick whose result drives both the disc and the actual placement
		// (DigitizationModule._pickPointCloudPosition), see frontLayerPick.js.
		this.frontParams = Object.assign({}, FRONT_PARAMS);
		this.frontCtx = deps.frontCtx || createFrontContext();
		this._pickFront = deps.pickFront || pickFrontSurface;
		this._frontCache = {valid: false, x: 0, y: 0, t: 0, cam: new Float64Array(7), result: null};
		this.frontStats = {fresh: 0, cacheHits: 0, picks: 0, followFrames: 0, beyond: 0, blendsSame: 0, blendsOther: 0};
		// authoritative plane (last pick) + the previous one, for the short time-based blend
		this.plane = {valid: false, x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 1, certain: false, cx: 0, cy: 0, cz: 0};
		this.prevPlane = {valid: false, x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 1, certain: false, cx: 0, cy: 0, cz: 0};
		this.blendStart = 0; this.blendMs = 0;
		this._rc = new THREE.Raycaster();
		this._tNew = new THREE.Vector3(); this._tOld = new THREE.Vector3(); this._qNew = new THREE.Quaternion(); this._qOld = new THREE.Quaternion();
		this._o = new THREE.Vector3(); this._d = new THREE.Vector3(); this._nn = new THREE.Vector3();

		this.indicator = null;
		this.hasHit = false;
		this._q = new THREE.Quaternion();
		this._n = new THREE.Vector3();
		this._z = new THREE.Vector3(0, 0, 1);
		this._pos = new THREE.Vector3();

		this._onUpdate = () => this.update();
		this._onLeave = () => { this.dirty = false; this.plane.valid = false; this._hide(); };
		viewer.addEventListener("update", this._onUpdate);
		this.domElement.addEventListener("mouseleave", this._onLeave, false);
	}

	// Called from InputHandler.onMouseMove for every raw mousemove: just records
	// state - the analysis itself is driven by the frame loop.
	notifyMouseMove(e, t = this._now()){
		if(!this.viewer.detection) return;
		if(e.buttons !== 0){
			// orbit / pan / drag: no analysis, and the disc would only lag
			this.navigating = true;
			this.dirty = false;
			this.plane.valid = false;
			this.intent.reset();
			this._hide();
			return;
		}
		this.navigating = false;
		this.mouse.x = this.input.mouse.x;
		this.mouse.y = this.input.mouse.y;
		this.intent.push(this.mouse.x, this.mouse.y, t);
		this.dirty = true;
	}

	// Per frame (viewer "update" event). The viewer renders ON DEMAND (viewer.shouldRedraw: only when something marked it
	// dirty - camera, material, loading nodes, drag - plus a 300 ms safety net). Moving the disc changes the scene but marks
	// nothing dirty, so once the point cloud has finished loading (no more "loading nodes" dirty source) the disc was only
	// RENDERED ~3 times a second although it was updated every frame - the "hak efter 8-10 s". _markIfChanged() asks for a
	// redraw exactly when the disc looks different from the last frame (never while it rests).
	update(){
		this._update();
		this._markIfChanged();
	}

	_markIfChanged(){
		const ind = this.indicator;
		if(!ind) return;
		const p = ind.position, q = ind.quaternion, g = this._sig || (this._sig = {v: false, o: -1, s: -1, px: 0, py: 0, pz: 0, qx: 0, qy: 0, qz: 0, qw: 1});
		const visible = ind.visible, o = ind.material.opacity;
		if(!visible && !g.v) return;                                  // hidden and was hidden: nothing to redraw
		if(visible === g.v && o === g.o && ind.scale.x === g.s && p.x === g.px && p.y === g.py && p.z === g.pz &&
			q.x === g.qx && q.y === g.qy && q.z === g.qz && q.w === g.qw) return;
		g.v = visible; g.o = o; g.s = ind.scale.x; g.px = p.x; g.py = p.y; g.pz = p.z; g.qx = q.x; g.qy = q.y; g.qz = q.z; g.qw = q.w;
		const viewer = this.viewer;
		if(typeof viewer._requestRedraw === "function") viewer._requestRedraw(); else viewer.needsRedraw = true;
		this.stats.redrawRequests = (this.stats.redrawRequests || 0) + 1;
	}

	_update(){
		const viewer = this.viewer;
		if(!viewer.detection){
			if(this.indicator && this.indicator.visible) this._hide();
			return;
		}

		const t = this._now();
		const dt = this.lastFrameAt ? Math.min(100, t - this.lastFrameAt) : 16;
		this.lastFrameAt = t;

		this._updateFront(t, dt);
	}

	// ── forreste lag ─────────────────────────────────────────────────────────────────────────
	// The front-surface result for a canvas-relative mouse position. Reuses the last result when it was
	// computed for (nearly) the same mouse position, from the same camera, a moment ago - so a click right
	// after the disc was drawn does NOT trigger a second GPU pick, and the registered position is exactly the
	// surface the disc showed.
	frontPick(mouse, opts = {}){
		const viewer = this.viewer;
		const camera = viewer.scene.getActiveCamera();
		const c = this._frontCache;
		const tolPx = opts.tolPx !== undefined ? opts.tolPx : MIN_MOVE_PX;
		const maxAge = opts.maxAgeMs !== undefined ? opts.maxAgeMs : 500;
		const now = this._now();
		const camKey = this._cameraKey(camera);
		if(!opts.fresh && c.valid && now - c.t <= maxAge &&
			Math.abs(mouse.x - c.x) <= tolPx && Math.abs(mouse.y - c.y) <= tolPx && this._sameCamera(camKey)){
			this.frontStats.cacheHits++;
			return c.result;
		}
		this.frontStats.fresh++;
		const r = this._pickFront(viewer, mouse, camera, viewer.scene.pointclouds, this.frontParams, this.frontCtx, this._now);
		this.frontStats.picks = this.frontCtx.stats.picks;
		c.valid = true; c.x = mouse.x; c.y = mouse.y; c.t = this._now(); c.result = r;
		for(let i = 0; i < 7; i++) c.cam[i] = camKey[i];
		return r;
	}

	_cameraKey(camera){
		const k = this._camKeyScratch || (this._camKeyScratch = new Float64Array(7));
		const p = camera.position, q = camera.quaternion;
		k[0] = p.x; k[1] = p.y; k[2] = p.z; k[3] = q.x; k[4] = q.y; k[5] = q.z; k[6] = camera.isPerspectiveCamera ? camera.fov : camera.zoom;
		return k;
	}

	_sameCamera(k){
		const c = this._frontCache.cam;
		for(let i = 0; i < 7; i++) if(c[i] !== k[i]) return false;
		return true;
	}

	// ── forreste lag: per-frame update ───────────────────────────────────────────────────────────────────────
	// Scheduling (MouseIntent re-used, nothing new): the pick rate depends on how the pointer moves -
	//   fast sweep  -> a pick at most every FRONT_FAST_PICK_INTERVAL_MS (the disc keeps following via the plane),
	//   slow        -> the cost-adaptive interval as before (cost / 0.3, 16-100 ms),
	//   stopped     -> MouseIntent's "resumed" triggers one pick at once, so the disc settles on the true surface.
	// The disc itself moves every frame (_followFront), independent of the pick rate and of FPS.
	_updateFront(t, dt){
		const stats = this.stats;
		let fast = false;
		if(this.intentGate && !this.navigating){
			const st = this.intent.update(t);
			if(st.fast && !stats.fast) stats.fastEntries++;
			stats.fast = st.fast;
			stats.speed = st.speed;
			fast = st.fast;
			if(st.resumed){
				stats.resumes++;
				this.lastAt = 0;
				this.lastAnalysed.x = -1e9;
				this.dirty = true;
			}
		}else{
			stats.fast = false;
		}

		if(this.dirty && !this.navigating){
			const base = Math.min(MAX_INTERVAL_MS, Math.max(MIN_INTERVAL_MS, stats.lastTotalMs / TARGET_DUTY));
			const interval = fast ? Math.max(base, FRONT_FAST_PICK_INTERVAL_MS) : base;
			stats.interval = interval;
			if(t - this.lastAt < interval){
				stats.skippedThrottle++;
			}else if(Math.abs(this.mouse.x - this.lastAnalysed.x) + Math.abs(this.mouse.y - this.lastAnalysed.y) < MIN_MOVE_PX){
				stats.skippedNoMove++;
				this.dirty = false;
			}else{
				this.dirty = false;
				this.lastAt = t;
				this.lastAnalysed.x = this.mouse.x;
				this.lastAnalysed.y = this.mouse.y;
				this._analyseFront(t);
			}
		}

		this._followFront(t, dt);
	}

	// Ray from the CURRENT cursor position intersected with `plane` (a camera-facing plane through the same
	// point when the plane is edge-on). Writes the point to `out`; returns false when there is no usable
	// intersection. `this._lastBeyond` tells whether the point had to be clamped to the trusted distance.
	_targetOnPlane(plane, out, camera, clientW, clientH){
		const rc = this._rc;
		rc.setFromCamera({x: (this.mouse.x / clientW) * 2 - 1, y: -(this.mouse.y / clientH) * 2 + 1}, camera);
		const O = this._o.copy(rc.ray.origin), D = this._d.copy(rc.ray.direction);
		let nx = plane.nx, ny = plane.ny, nz = plane.nz;
		let denom = D.x * nx + D.y * ny + D.z * nz;
		if(Math.abs(denom) < FRONT_MIN_GRAZING){
			this._nn.set(O.x - plane.x, O.y - plane.y, O.z - plane.z).normalize();
			nx = this._nn.x; ny = this._nn.y; nz = this._nn.z;
			denom = D.x * nx + D.y * ny + D.z * nz;
			if(Math.abs(denom) < 1e-6) return false;
		}
		const tt = ((plane.x - O.x) * nx + (plane.y - O.y) * ny + (plane.z - O.z) * nz) / denom;
		if(!(tt > 0)) return false;
		let px = O.x + D.x * tt, py = O.y + D.y * tt, pz = O.z + D.z * tt;
		const pw = this._pixelWorld(camera, tt, clientH);
		const limit = FRONT_EXTRAP_PX * pw;
		let dx = px - plane.x, dy = py - plane.y, dz = pz - plane.z;
		const dist = Math.hypot(dx, dy, dz);
		this._lastBeyond = dist > limit;
		if(this._lastBeyond){
			const k = limit / dist;
			px = plane.x + dx * k; py = plane.y + dy * k; pz = plane.z + dz * k;
		}
		// (cx,cy,cz): the in-plane offset between the cursor ray and the authoritative point at pick time, so the
		// disc rests exactly on the point placement would use, and keeps that offset while it follows the cursor
		out.set(px + (plane.cx || 0), py + (plane.cy || 0), pz + (plane.cz || 0));
		return true;
	}

	_pixelWorld(camera, t, heightPx){
		if(camera.isOrthographicCamera) return Math.abs(camera.top - camera.bottom) / (camera.zoom || 1) / heightPx;
		return 2 * t * Math.tan((camera.fov * Math.PI / 180) / 2) / heightPx;
	}

	_planeQuaternion(plane, camera, out){
		if(plane.certain) this._n.set(plane.nx, plane.ny, plane.nz);
		else this._n.set(camera.position.x - plane.x, camera.position.y - plane.y, camera.position.z - plane.z).normalize();
		return out.setFromUnitVectors(this._z, this._n);
	}

	_followFront(t, dt){
		const viewer = this.viewer;
		const plane = this.plane;
		this._stepFade(dt, this.navigating || !plane.valid);
		if(!plane.valid || !this.indicator) return;
		const camera = viewer.scene.getActiveCamera();
		const el = viewer.renderer.domElement;
		const W = el.clientWidth || 1, H = el.clientHeight || 1;

		const ok = this._targetOnPlane(plane, this._tNew, camera, W, H);
		if(!ok) return;
		let beyond = this._lastBeyond;
		this._planeQuaternion(plane, camera, this._qNew);

		let w = 1;
		if(this.blendMs > 0 && this.prevPlane.valid){
			w = Math.min(1, Math.max(0, (t - this.blendStart) / this.blendMs));
			if(w < 1){
				if(this._targetOnPlane(this.prevPlane, this._tOld, camera, W, H)){
					const e = 1 - (1 - w) * (1 - w);                 // ease-out
					this._tNew.lerpVectors(this._tOld, this._tNew, e);
					this._planeQuaternion(this.prevPlane, camera, this._qOld);
					this._qOld.slerp(this._qNew, e); this._qNew.copy(this._qOld);
				}
			}else{
				this.prevPlane.valid = false;
			}
		}
		this.frontStats.followFrames++;
		if(beyond){
			this.frontStats.beyond++;
			this.dirty = true;                                     // we are extrapolating too far: ask for a fresh pick
		}
		this.indicator.position.copy(this._tNew);
		this.indicator.quaternion.copy(this._qNew);
		this.hasHit = true;
		this.baseOpacity = plane.certain ? (beyond ? OPACITY_CERTAIN * 0.6 : OPACITY_CERTAIN) : OPACITY_UNCERTAIN;
		this._applyFade();
		this._updateScale();
	}

	_analyseFront(t0){
		const viewer = this.viewer;
		const stats = this.stats;
		stats.updates++;
		stats.picks++;
		const r = this.frontPick(this.mouse, {fresh: true});
		const t1 = this._now();
		stats.lastPickMs = this.frontCtx.stats.lastPickMs;
		stats.lastAnalyseMs = this.frontCtx.stats.lastSelectMs;
		stats.lastTotalMs = t1 - t0;
		if(!r || !r.ok){
			stats.noHit++;
			this.plane.valid = false;
			this.prevPlane.valid = false;
			this._hide();
			return;
		}
		stats.lastPoints = r.layerSize; stats.lastNodes = 0; stats.lastNodesVisible = 0;
		stats.lastFailure = r.lowConfidence ? "lowConfidence" : (r.accepted ? null : "noPlane");

		const camera = viewer.scene.getActiveCamera();
		const hit = this.hit, cam = this.camera;
		hit.x = r.location.x; hit.y = r.location.y; hit.z = r.location.z;
		cam.x = camera.position.x; cam.y = camera.position.y; cam.z = camera.position.z;

		this._ensureIndicator();
		this._setPlane(r, camera, t0);
		stats.lastTotalMs = this._now() - t0;
	}

	// Promote the pick result to the authoritative plane. The previous plane stays for a short, time-based
	// blend: the disc position is computed from the cursor ray against BOTH planes and mixed by weight, so it
	// keeps following the cursor during the blend (no glide through free space). Same surface -> 80 ms,
	// another surface/depth -> 40 ms; the very first plane appears without blend.
	_setPlane(r, camera, t){
		const cur = this.plane, prev = this.prevPlane;
		const certain = !!(r.accepted && !r.lowConfidence);
		if(cur.valid){
			prev.valid = true; prev.x = cur.x; prev.y = cur.y; prev.z = cur.z; prev.nx = cur.nx; prev.ny = cur.ny; prev.nz = cur.nz; prev.certain = cur.certain; prev.cx = cur.cx; prev.cy = cur.cy; prev.cz = cur.cz;
		}else{
			prev.valid = false;
		}
		cur.valid = true; cur.x = r.location.x; cur.y = r.location.y; cur.z = r.location.z;
		cur.certain = certain;
		if(certain){ cur.nx = r.normal.x; cur.ny = r.normal.y; cur.nz = r.normal.z; }
		else{
			const dx = camera.position.x - cur.x, dy = camera.position.y - cur.y, dz = camera.position.z - cur.z;
			const l = Math.hypot(dx, dy, dz) || 1;
			cur.nx = dx / l; cur.ny = dy / l; cur.nz = dz / l;
		}
		this.blendMs = 0;
		const el = this.viewer.renderer.domElement;
		const W = el.clientWidth || 1, H = el.clientHeight || 1;
		cur.cx = 0; cur.cy = 0; cur.cz = 0;
		if(this._targetOnPlane(cur, this._tNew, camera, W, H) && !this._lastBeyond){
			cur.cx = cur.x - this._tNew.x; cur.cy = cur.y - this._tNew.y; cur.cz = cur.z - this._tNew.z;
		}
		if(prev.valid){
			if(this._targetOnPlane(prev, this._tOld, camera, W, H) && this._targetOnPlane(cur, this._tNew, camera, W, H)){
				const pw = this._pixelWorld(camera, Math.max(1e-6, camera.position.distanceTo(this._tNew)), H);
				const same = this._tOld.distanceTo(this._tNew) <= FRONT_SAME_SURFACE_PX * pw;
				this.blendMs = same ? FRONT_BLEND_SAME_MS : FRONT_BLEND_OTHER_MS;
				if(same) this.frontStats.blendsSame++; else this.frontStats.blendsOther++;
				this.blendStart = t;
			}
		}
		this.hasHit = true;
	}

	// move the fade toward 0 (fast movement / stale position) or 1 (fresh result)
	_stepFade(dt, hide){
		if(hide) this.fade = Math.max(0, this.fade - dt / FADE_OUT_MS);
		else this.fade = Math.min(1, this.fade + dt / FADE_IN_MS);
		if(this.indicator && (this.indicator.visible || this.fade > 0)) this._applyFade();
	}

	_applyFade(){
		const ind = this.indicator;
		if(!ind) return;
		ind.material.opacity = this.baseOpacity * this.fade;
		ind.visible = !!this.hasHit && ind.material.opacity > 0;
	}

	_updateScale(){
		const viewer = this.viewer;
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(this._size || (this._size = new THREE.Vector2()));
		const s = this._scale(camera, this.indicator.position, camera.position, size.width, size.height, INDICATOR_RADIUS_PX);
		this.indicator.scale.set(s, s, s);
	}

	_ensureIndicator(){
		if(!this.indicator){
			// radius-1 disc (scaled to a constant pixel size), double sided so it is
			// visible from either side; drawn in the 'climb' pass on top of everything
			this.indicator = new THREE.Mesh(
				new THREE.CircleGeometry(1, 32),
				new THREE.MeshBasicMaterial({
					color: 0xf0f0f0,
					side: THREE.DoubleSide,
					depthTest: false,
					depthWrite: false,
					transparent: true,
					opacity: OPACITY_CERTAIN,
				})
			);
			this.indicator.visible = false;
			this.indicator.name = "surfaceDetectionIndicator";
		}
		const climb = this.viewer.scene.climb;
		if(this.indicator.parent !== climb) climb.add(this.indicator);
	}

	_hide(){
		this.hasHit = false;
		this.fade = 0;
		if(this.indicator) this.indicator.visible = false;
	}

	dispose(){
		this.viewer.removeEventListener("update", this._onUpdate);
		this.domElement.removeEventListener("mouseleave", this._onLeave, false);
		if(this.indicator && this.indicator.parent) this.indicator.parent.remove(this.indicator);
	}
}
